/**
 * @param {string | number | boolean | null | undefined} value
 * @returns {string} Safe for .toLowerCase() after this step (always a string).
 */
export function normalizeTextForFilter(value) {
  if (typeof value === "string") {
    return value;
  }
  if (value == null) {
    return "";
  }
  return String(value);
}

/**
 * @param {string | number | boolean | null | undefined} value
 * @returns {string}
 */
function safeTrimString(value) {
  return normalizeTextForFilter(value).trim();
}

/**
 * @param {object | null | undefined} accion
 * @returns {string | number | boolean | object | null | undefined}
 */
function accionTargetDisplayNombre(accion) {
  const snap = accion?.idRecursoSnapshot;
  if (snap != null && typeof snap === "object" && !Array.isArray(snap)) {
    const v = /** @type {{ nombre?: unknown }} */ (snap).nombre;
    if (v != null) {
      return v;
    }
  }
  const ir = accion?.idRecurso;
  if (ir != null && typeof ir !== "object") {
    return ir;
  }
  return ir?.nombre;
}

/**
 * @param {object | null | undefined} accion
 * @returns {string | number | boolean | object | null | undefined}
 */
function accionTargetDisplayRol(accion) {
  const snap = accion?.idRecursoSnapshot;
  if (snap != null && typeof snap === "object" && !Array.isArray(snap)) {
    const v = /** @type {{ rol?: unknown }} */ (snap).rol;
    if (v != null) {
      return v;
    }
  }
  const ir = accion?.idRecurso;
  if (ir != null && typeof ir === "object" && !Array.isArray(ir)) {
    return ir.rol;
  }
  return undefined;
}

/**
 * @param {string | number | Date | null | undefined} raw
 * @returns {number | null} Epoch ms, or null if missing / not parseable.
 */
function parseFiniteTime(raw) {
  if (raw == null || raw === "") {
    return null;
  }
  if (typeof raw === "string" && raw.trim() === "") {
    return null;
  }
  const t = raw instanceof Date ? raw.getTime() : new Date(raw).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * Primary event time for sorting/filtering: first valid `fecha`, else `createdAt`, else 0.
 * @param {object | null | undefined} item
 * @returns {number}
 */
function getTimestamp(item) {
  if (item == null || typeof item !== "object") {
    return 0;
  }
  const rec = /** @type {{ fecha?: string | number | Date, createdAt?: string | number | Date }} */ (
    item
  );
  const fromFecha = parseFiniteTime(rec.fecha);
  if (fromFecha != null) {
    return fromFecha;
  }
  const fromCreated = parseFiniteTime(rec.createdAt);
  return fromCreated != null ? fromCreated : 0;
}

/**
 * Case-insensitive substring match; avoids TypeError on non-string name/text.
 * @param {string | number | boolean | object | null | undefined} name
 * @param {string | number | boolean | object | null | undefined} text
 * @returns {boolean}
 */
export function textIncludesCaseInsensitive(name, text) {
  const normalizedName = normalizeTextForFilter(name);
  const normalizedText = normalizeTextForFilter(text);
  if (normalizedText === "") {
    return true;
  }
  return normalizedName.toLowerCase().includes(normalizedText.toLowerCase());
}

/**
 * @param {Date} d
 * @returns {string} YYYY-MM-DD in UTC (aligns date-only filter inputs with stored UTC timestamps).
 */
function toUTCDateKey(d) {
  return d.toISOString().slice(0, 10);
}

/**
 * @param {object} accion
 * @param {string | number | boolean | object | null | undefined} text Search text from UI
 * @returns {boolean}
 */
export function accionIdRecursoNombreMatchesText(accion, text) {
  const n = accionTargetDisplayNombre(accion);
  return textIncludesCaseInsensitive(n, text);
}

/**
 * @param {object} accion
 * @param {string | number | boolean | object | null | undefined} text Search text from UI
 * @returns {boolean}
 */
export function accionIdRecursoRolMatchesText(accion, text) {
  const n = accionTargetDisplayRol(accion);
  return textIncludesCaseInsensitive(n, text);
}

/**
 * @param {object[]} list
 * @returns {object[]}
 */
export function sortAccionesByFechaDesc(list) {
  if (!Array.isArray(list)) {
    return [];
  }
  return [...list].sort((a, b) => {
    const ta = getTimestamp(a);
    const tb = getTimestamp(b);
    return tb - ta;
  });
}

/**
 * @typedef {{
 *   nombre: string,
 *   accionTipo: string,
 *   fechaDesde: string,
 *   fechaHasta: string,
 *   rol: string
 * }} AccionesFiltrosState
 */

/**
 * @param {object[] | null | undefined} list
 * @param {AccionesFiltrosState | null | undefined} filtros
 * @returns {object[]}
 */
export function applyAccionesFiltros(list, filtros) {
  if (list == null || !Array.isArray(list)) {
    return [];
  }
  if (filtros == null) {
    return [...list];
  }
  const nombre = normalizeTextForFilter(filtros.nombre).trim().toLowerCase();
  const accionTipo = normalizeTextForFilter(
    filtros.accionTipo == null ? "" : filtros.accionTipo,
  )
    .trim()
    .toLowerCase();
  const fechaDesde =
    typeof filtros.fechaDesde === "string" ? filtros.fechaDesde.trim() : "";
  const fechaHasta =
    typeof filtros.fechaHasta === "string" ? filtros.fechaHasta.trim() : "";
  const rol = normalizeTextForFilter(filtros.rol).trim().toLowerCase();

  return list.filter((accion) => {
    if (nombre !== "") {
      const targetName = safeTrimString(accionTargetDisplayNombre(accion)).toLowerCase();
      const actorName = safeTrimString(accion?.ejecutadoPor?.nombre).toLowerCase();
      if (!targetName.includes(nombre) && !actorName.includes(nombre)) {
        return false;
      }
    }
    if (accionTipo !== "") {
      const t = normalizeTextForFilter(accion?.tipo).trim().toLowerCase();
      if (t !== accionTipo) {
        return false;
      }
    }
    if (rol !== "") {
      const r = safeTrimString(accionTargetDisplayRol(accion)).toLowerCase();
      if (!r.includes(rol)) {
        return false;
      }
    }
    if (fechaDesde !== "" || fechaHasta !== "") {
      const ts = getTimestamp(accion);
      if (ts === 0) {
        return false;
      }
      const d = new Date(ts);
      const key = toUTCDateKey(d);
      if (fechaDesde !== "" && key < fechaDesde) {
        return false;
      }
      if (fechaHasta !== "" && key > fechaHasta) {
        return false;
      }
    }
    return true;
  });
}
